import React, { useState, useRef, useEffect } from 'react';
import LoaderOverlay from "./LoaderOverlay";
import styles from '../styles/HeaderGlobal.module.css';
import { hasStudentRole, useAuth } from '../api/AuthContext';
import { useNavigate, NavLink } from 'react-router-dom';
import { useSidebar } from './Sidebar';


const HeaderGlobal = ({
  title = '',
  institution,
  onInstitutionChange,
  campus,
  onCampusChange,
  notifications = [],
  onLogout, // opcional, si no se pasa, se usa el default
  children,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [institutionsData, setInstitutionsData] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(() => {
    // Try to restore from localStorage
    return localStorage.getItem('selectedInstitution') || '';
  });
  const [campuses, setCampuses] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState(() => {
    return localStorage.getItem('selectedCampus') || '';
  });

  // Sincronizar selectedCampus con prop campus si cambia desde el padre
  useEffect(() => {
    if (typeof campus === 'string' && campus !== selectedCampus) {
      setSelectedCampus(campus);
    }
  }, [campus]);

  // Notificar siempre cambios de campus (manuales o automáticos) al resto de pantallas.
  useEffect(() => {
    if (!selectedCampus) return;
    window.dispatchEvent(new CustomEvent('campusChanged', { detail: { campus: selectedCampus } }));
  }, [selectedCampus]);
  const [studentDefaultsApplied, setStudentDefaultsApplied] = useState(false);
  const [loadingStudentData, setLoadingStudentData] = useState(false);
  const menuRef = useRef(null);
  const iconRef = useRef(null);
  const { logout, user: authUser } = useAuth();
  // Tomar SIEMPRE los datos del contexto
  const displayUser = authUser || {};
  // Usar nombre_completo y rol_nombre si existen, si no, fallback
  const fullName = displayUser.nombre_completo || displayUser.first_name || displayUser.last_name ?
    `${displayUser.nombre_completo || `${displayUser.first_name || ''} ${displayUser.last_name || ''}`.trim()}` :
    (displayUser.username || displayUser.email || 'Admin');
  const email = displayUser.email || '';
  const userRole = displayUser.rol_nombre || displayUser.rol || '';
  const isStudent = hasStudentRole(displayUser);
  const isTeacher = userRole && userRole.toLowerCase().includes('docente');
  const isBlocked = isStudent || isTeacher;
  const headerClassName = `${styles.headerGlobal} ${isStudent ? styles.headerGlobalStudent : ''}`;
  const titleStyle = isStudent ? { color: '#ffffff' } : {};
  const headerRowStyle = {
    display: 'flex',
    alignItems: isStudent ? 'flex-end' : 'center',
    gap: 16,
  };
  const headerRightTextColor = isStudent ? '#ffffff' : '#222';
  const headerIconStrokeColor = isStudent ? '#ffffff' : '#898d8d';

  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  // Fetch campuses when selectedInstitution changes (solo para NO estudiantes)
  useEffect(() => {
    // Para estudiantes, la carga de campus se hace en un efecto específico
    if (isStudent) {
      return;
    }

    if (!selectedInstitution) {
      setCampuses([]);
      setSelectedCampus('');
      return;
    }
    // Si se selecciona "Todos", obtener todos los campus activos
    if (selectedInstitution === 'Todos') {
      fetch(process.env.REACT_APP_API_BASE + '/catalogos/campus/')
        .then(res => res.json())
        .then(campData => {
          if (Array.isArray(campData)) {
            setCampuses(campData);
            // Si el campus guardado es 'Todos', restaurar esa opción
            const savedCampus = localStorage.getItem('selectedCampus');
            if (savedCampus === 'Todos') {
              setSelectedCampus('Todos');
            } else if (savedCampus && campData.some(c => c.nombre_campus === savedCampus)) {
              setSelectedCampus(savedCampus);
            } else if (campData.length > 0) {
              setSelectedCampus(campData[0].nombre_campus);
              localStorage.setItem('selectedCampus', campData[0].nombre_campus);
            } else {
              setSelectedCampus('');
              localStorage.removeItem('selectedCampus');
            }
          }
        })
        .catch(() => {
          setCampuses([]);
          setSelectedCampus('');
        });
      return;
    }
    // Fetch all institutions to get the id de la institución seleccionada
    // Si la institución es 'Todos', no filtrar por id, solo restaurar campus
    if (selectedInstitution === 'Todos') {
      // Ya se maneja en el bloque anterior, no hacer nada aquí
      return;
    }
    fetch(process.env.REACT_APP_API_BASE + '/catalogos/instituciones/')
      .then(res => res.json())
      .then(instData => {
        const instObj = Array.isArray(instData)
          ? instData.find(i => i.nombre_institucion === selectedInstitution)
          : null;
        if (!instObj) {
          setCampuses([]);
          setSelectedCampus('');
          return;
        }
        // Fetch campuses filtered by institution id
        fetch(process.env.REACT_APP_API_BASE + `/catalogos/campus/?id_institucion=${instObj.id_institucion}`)
          .then(res => res.json())
          .then(campData => {
            if (Array.isArray(campData)) {
              setCampuses(campData);
              // Si el campus guardado es 'Todos', restaurar esa opción (solo para NO estudiantes)
              const savedCampus = localStorage.getItem('selectedCampus');
              if (savedCampus === 'Todos') {
                setSelectedCampus('Todos');
              } else if (savedCampus && campData.some(c => c.nombre_campus === savedCampus)) {
                setSelectedCampus(savedCampus);
              } else if (campData.length > 0) {
                setSelectedCampus(campData[0].nombre_campus);
                localStorage.setItem('selectedCampus', campData[0].nombre_campus);
              } else {
                setSelectedCampus('');
                localStorage.removeItem('selectedCampus');
              }
            }
          })
          .catch(() => {
            setCampuses([]);
            setSelectedCampus('');
          });
      })
      .catch(() => {
        setCampuses([]);
        setSelectedCampus('');
      });
  }, [selectedInstitution, isStudent]);

  // Handler por defecto para logout
  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      if (onLogout) {
        await onLogout();
      } else {
        await logout();
        navigate('/login');
      }
    } finally {
      setLogoutLoading(false);
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    // Fetch institutions from API
    fetch(process.env.REACT_APP_API_BASE + '/catalogos/instituciones/')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const names = data.map(inst => inst.nombre_institucion);
          setInstitutions(names);
          setInstitutionsData(data);
          // Si el valor guardado es 'Todos', restaurarlo y disparar la carga de campus para 'Todos'
          const savedInstitution = localStorage.getItem('selectedInstitution');
          if (savedInstitution === 'Todos' && names.length > 1 && selectedInstitution !== 'Todos') {
            setSelectedInstitution('Todos');
          } else if (names.length === 1 && selectedInstitution !== names[0]) {
            setSelectedInstitution(names[0]);
            localStorage.setItem('selectedInstitution', names[0]);
            setCampuses([]);
            setSelectedCampus('');
            localStorage.removeItem('selectedCampus');
          } else if (names.length > 0 && !localStorage.getItem('selectedInstitution')) {
            setSelectedInstitution(names[0]);
          }
        }
      })
      .catch(() => {
        setInstitutions([]);
        setSelectedInstitution('');
      });

    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && iconRef.current && !iconRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  // Si el prop institution cambia, sincroniza el valor local
  useEffect(() => {
    if (institution) {
      setSelectedInstitution(institution);
    }
  }, [institution]);

  // Limpiar selección persistida cuando el usuario es estudiante
  useEffect(() => {
    if (isStudent) {
      localStorage.removeItem('selectedInstitution');
      localStorage.removeItem('selectedCampus');
      setSelectedInstitution('');
      setSelectedCampus('');
    }
  }, [isStudent]);

  // Para estudiantes: obtener campus desde /sistema/usuarios-campus/mis-campus/
  useEffect(() => {
    if (!isStudent) return;
    if (studentDefaultsApplied) return;

    setLoadingStudentData(true);
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoadingStudentData(false);
      return;
    }

    const baseUrl = process.env.REACT_APP_API_BASE;

    const loadStudentData = async () => {
      try {
        // Obtener datos del usuario para la institución
        const meRes = await fetch(baseUrl + '/sistema/usuarios/me/', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const meData = await meRes.json();
        if (!meRes.ok || !meData) {
          setStudentDefaultsApplied(true);
          setLoadingStudentData(false);
          return;
        }

        const { id_institucion, institucion_nombre } = meData;

        // Fijar institución y limitar el select solo a esa
        let institutionName = institucion_nombre || '';
        let filteredInstitutions = [];
        if (!institutionName && id_institucion && institutionsData.length) {
          const instId = String(id_institucion);
          const instObj = institutionsData.find(i => String(i.id_institucion) === instId);
          if (instObj) {
            institutionName = instObj.nombre_institucion;
          }
        }
        if (institutionName) {
          setSelectedInstitution(institutionName);
          filteredInstitutions = [institutionName];
        }
        setInstitutions(filteredInstitutions);

        // Obtener campus del usuario autenticado
        const campusRes = await fetch(baseUrl + '/sistema/usuarios-campus/mis-campus/', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const campusData = await campusRes.json();
        if (Array.isArray(campusData)) {
          setCampuses(campusData);
          // Si solo hay un campus, seleccionarlo automáticamente
          if (campusData.length === 1) {
            setSelectedCampus(campusData[0].nombre_campus);
          } else if (campusData.length > 1) {
            const savedCampus = localStorage.getItem('selectedCampus');
            if (savedCampus && campusData.some(c => c.nombre_campus === savedCampus)) {
              setSelectedCampus(savedCampus);
            } else {
              setSelectedCampus(campusData[0].nombre_campus);
              localStorage.setItem('selectedCampus', campusData[0].nombre_campus);
            }
          } else {
            setSelectedCampus('');
          }
        }
      } catch (e) {
        setCampuses([]);
        setSelectedCampus('');
      } finally {
        setStudentDefaultsApplied(true);
        setLoadingStudentData(false);
      }
    };

    loadStudentData();
  }, [
    isStudent,
    institutionsData,
    selectedInstitution,
    studentDefaultsApplied,
  ]);

  return (
    <>
      {logoutLoading && (
        <LoaderOverlay visible={true} label="Cerrando Sesión..." />
      )}
      <div className={headerClassName}>
        <div className={styles.headerContent}>
            <div style={headerRowStyle}>
              {isStudent && (
                <NavLink
                  to="/inicio"
                  style={{
                    textDecoration: 'none',
                    display: 'inline-flex',
                    flex: '0 0 auto',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      marginTop: 4,
                      marginBottom: 4,
                    }}
                  >
                    <div
                      style={{ marginBottom: 4, width: '100%', display: 'flex', justifyContent: 'center' }}
                    >
                      <img
                        src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/5e7b82052c97d59080050b9babf25376d2930ca3?placeholderIfAbsent=true"
                        alt="Universidad Lux Logo"
                        style={{ height: 40, width: 'auto', display: 'block' }}
                      />
                    </div>
                    {!isCollapsed && (
                      <div
                        style={{
                          fontSize: 12,
                          lineHeight: 1.1,
                          marginBottom: 0,
                          letterSpacing: 0.5,
                          color: '#ffffff',
                          fontWeight: 700,
                          textAlign: 'center',
                          width: '100%',
                        }}
                      >
                        UNIVERSIDAD
                        <span style={{ fontWeight: 400, margin: '0 4px' }}>|</span>
                        <span style={{ color: '#fff700', fontWeight: 700 }}>LUX</span>
                      </div>
                    )}
                  </div>
                </NavLink>
              )}
              <h1 className={styles.headerTitle} style={titleStyle}>{title}</h1>
            </div>
          {children}
        </div>
        <div className={styles.headerRight}>
          <div className={styles.headerIcons}>
            <button className={styles.iconButton}>
              {/* Bell icon SVG */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 17H20L18.5951 15.5951C18.2141 15.2141 18 14.6973 18 14.1585V11C18 8.38757 16.3304 6.16509 14 5.34142V5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5V5.34142C7.66962 6.16509 6 8.38757 6 11V14.1585C6 14.6973 5.78595 15.2141 5.40493 15.5951L4 17H9M15 17V18C15 19.6569 13.6569 21 12 21C10.3431 21 9 19.6569 9 18V17M15 17H9" stroke={headerIconStrokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className={styles.institutionSelector}>
            <select
              className={styles.institutionSelect}
              value={selectedInstitution}
              disabled={isStudent || institutions.length === 1 || loadingStudentData}
              onChange={e => {
                setSelectedInstitution(e.target.value);
                localStorage.setItem('selectedInstitution', e.target.value);
                setCampuses([]);
                setSelectedCampus('');
                localStorage.removeItem('selectedCampus');
                if (onInstitutionChange) onInstitutionChange(e);
              }}
            >
              {loadingStudentData ? (
                <option value="" disabled>
                  Cargando institución...
                </option>
              ) : (
                institutions.map((inst) => (
                  <option key={inst} value={inst}>{inst}</option>
                ))
              )}
            </select>
          </div>
          <div className={styles.campusSelector}>
            <select
              className={styles.campusSelect}
              value={selectedCampus}
              disabled={campuses.length === 0 || (isStudent && campuses.length <= 1) || loadingStudentData}
              onChange={e => {
                setSelectedCampus(e.target.value);
                localStorage.setItem('selectedCampus', e.target.value);
                // Emitir evento personalizado para notificar cambio inmediato
                window.dispatchEvent(new CustomEvent('campusChanged', { detail: { campus: e.target.value } }));
                if (onCampusChange) onCampusChange(e);
              }}
            >
              {loadingStudentData ? (
                <option value="" disabled>
                  Cargando campus...
                </option>
              ) : (
                campuses.length === 0 ? (
                  <option value="">Sin campus disponibles</option>
                ) : (
                  <>
                    {campuses.length > 1 && (
                      <option value="Todos">Todos los Campus</option>
                    )}
                    {campuses.map((camp) => (
                      <option key={camp.id_campus} value={camp.nombre_campus}>{camp.nombre_campus}</option>
                    ))}
                  </>
                )
              )}
            </select>
          </div>
          <div className={styles.userInfo} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              ref={iconRef}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 12px 4px 4px',
                borderRadius: 8,
                minWidth: 0,
                boxShadow: menuOpen ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                transition: 'box-shadow 0.2s',
              }}
            >
              {/* Dynamic color for avatar box */}
              <div style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: stringToColor(fullName || 'A'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 15,
                color: '#fff',
                textTransform: 'uppercase',
                flexShrink: 0,
                letterSpacing: 1,
                userSelect: 'none',
              }}>
                {fullName ? fullName.split(' ').map(n => n[0]).join('').slice(0,2) : 'A'}
              </div>
              <span style={{ fontWeight: 500, fontSize: 15, color: headerRightTextColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 }}>{fullName}</span>
              <svg
                className={styles.dropdownIcon}
                width="16"
                height="16"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginLeft: 2 }}
              >
                <path d="M3 4.5L6 7.5L9 4.5" stroke={headerIconStrokeColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {menuOpen && (
              <div
                ref={menuRef}
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: 0,
                  background: '#fff',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                  borderRadius: 12,
                  zIndex: 1000,
                  minWidth: 260,
                  padding: '0',
                  fontFamily: 'inherit',
                }}
              >
                {/* User Info con email arriba, luego nombre y rol */}
                <div style={{ padding: '18px 20px 10px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Email arriba */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', minWidth: 18, minHeight: 18, marginTop: 1 }}>
                      <rect x="3" y="5" width="18" height="14" rx="2"/>
                      <polyline points="3 7 12 13 21 7"/>
                    </svg>
                    <span style={{ fontSize: 15, color: '#555', fontWeight: 500, letterSpacing: 0.1, lineHeight: '18px', display: 'block' }}>{email}</span>
                  </div>
                  {/* Avatar, nombre y rol */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 20,
                      color: '#555',
                      flexShrink: 0,
                      textTransform: 'uppercase',
                    }}>
                      {fullName ? fullName.split(' ').map(n => n[0]).join('').slice(0,2) : 'AD'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: 16, color: '#222', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fullName}</span>
                      <span style={{ fontSize: 13, color: '#888', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userRole}</span>
                    </div>
                  </div>
                </div>
                {/* Divider */}
                <div style={{ height: 1, background: '#f0f0f0', margin: '8px 0 0 0' }} />
                {/* Restablecer contraseña Option */}
                <button
                  style={{
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    padding: '14px 20px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: '#0056b3',
                    fontWeight: 500,
                    fontSize: 15,
                    borderRadius: '0',
                    marginTop: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                  onClick={() => navigate('/restablecer-contrasena')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0056b3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17v-6"/><path d="M12 7h.01"/><circle cx="12" cy="12" r="10"/></svg>
                  Restablecer contraseña
                </button>
                {/* Logout Option */}
                <button
                  style={{
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    padding: '14px 20px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: '#c00',
                    fontWeight: 500,
                    fontSize: 15,
                    borderRadius: '0 0 12px 12px',
                    marginTop: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                  onClick={handleLogout}
                  disabled={logoutLoading}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Utility for pastel dynamic color
function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  // H: 0-360, S: 55-70%, L: 65-75% (pastel intenso)
  const h = Math.abs(hash) % 360;
  const s = 55 + (Math.abs(hash) % 15); // 55-69
  const l = 65 + (Math.abs(hash) % 10); // 65-74
  return `hsl(${h},${s}%,${l}%)`;
}

export default HeaderGlobal;
